<script setup lang="ts">
import benchConfigDarwin from '~/assets/pandacore-bench/tun-bench-darwin.yml?raw'
import benchConfigLinux from '~/assets/pandacore-bench/tun-bench-linux.yml?raw'
import benchPatch from '~/assets/pandacore-bench/tun-bench-pandacore.patch?raw'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import type { PairMetric, TunBenchPlatform } from '~/utils/pandaCoreBenchmarks'
import {
  cpuEfficiencyMetric,
  energyMetric,
  findPlatform,
  findThroughput,
  formatMultiplier,
  formatSignedPercent,
  gigabytesTransferred,
  memoryMetric,
  rankComparisons,
  platformMetrics,
  tallyVerdicts,
  throughputMetric,
} from '~/utils/pandaCoreBenchmarks'
import { tunBench } from '~/utils/pandaCoreTunBenchResults'

const { t, locale } = useI18n()

const fixed = (value: number, decimals: number) => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals,
}).format(value)

const comparisonViews = (metric: PairMetric) => rankComparisons(metric).map((comparison) => {
  const name = t(comparison.rival === 'singBox' ? 'pandacore.rival' : 'pandacore.rival2')
  return {
    ...comparison,
    name,
    caption: t('pandacore.throughput.vs_caption', { name }),
    multiplierText: formatMultiplier(comparison.multiplier),
    deltaText: formatSignedPercent(comparison.deltaPercent),
    verdictText: comparison.tie ? t('pandacore.throughput.verdict_tie') : comparison.leads ? t('pandacore.throughput.verdict_pandaCore') : t('pandacore.throughput.verdict_rival', { name }),
  }
})

/**
 * One tun-bench platform is shown at a time; both were measured with the same tool and matrix.
 * The Linux platform stays in the dataset but off the page for now; add 'linux' here to show
 * the platform switch again.
 */
type BenchPlatformId = TunBenchPlatform['id']
const shownPlatformIds: readonly BenchPlatformId[] = ['macos']
const benchPlatforms = tunBench.platforms
  .filter(platform => shownPlatformIds.includes(platform.id))
  .map(platform => ({ id: platform.id, label: platform.id === 'macos' ? 'macOS' : 'Linux' }))
if (benchPlatforms.length === 0) {
  throw new Error('The PandaCore page needs at least one benchmark platform to show')
}
const benchPlatformId = ref<BenchPlatformId>(benchPlatforms[0]!.id)
const platform = computed(() => findPlatform(tunBench, benchPlatformId.value))

const upload1 = computed(() => findThroughput(platform.value, 'upload', 1))
const upload8 = computed(() => findThroughput(platform.value, 'upload', 8))
const download1 = computed(() => findThroughput(platform.value, 'download', 1))
const download8 = computed(() => findThroughput(platform.value, 'download', 8))
/** Full-speed cells drive the headline; the full matrix also includes both capped directions. */
const unlimitedCells = computed(() => [upload1.value, download1.value, upload8.value, download8.value])
const cappedCells = computed(() => [findThroughput(platform.value, 'upload', 1, 1), findThroughput(platform.value, 'download', 1, 1)])
const allCells = computed(() => [...unlimitedCells.value, ...cappedCells.value])
const allMetrics = computed(() => platformMetrics(platform.value)
  .filter(metric => !cappedCells.value.some(cell => cell.id === metric.id)))
const tally = computed(() => tallyVerdicts(allMetrics.value.flatMap(metric => rankComparisons(metric)
  .map(comparison => comparison.tie ? 'tie' : comparison.leads ? 'pandaCore' : 'singBox'))))
const hasEngine2 = computed(() => Boolean(platform.value.engine2))
const hasEnergy = computed(() => platform.value.loadMetric === 'energy')

const memoryIdle = computed(() => memoryMetric(platform.value.memory, 0))
const memoryLoaded = computed(() => memoryMetric(platform.value.memory, platform.value.memory.maxConnections))
const footprintMetrics = computed<PairMetric[]>(() => platform.value.memory.pandaCore.points.map(point => memoryMetric(platform.value.memory, point.connections)))
const cpuMetrics = computed<PairMetric[]>(() => allCells.value.map(cpuEfficiencyMetric))
const energyMetrics = computed<PairMetric[]>(() => (hasEnergy.value ? unlimitedCells.value.map(energyMetric) : []))
const cappedEnergyMetrics = computed<PairMetric[]>(() => (hasEnergy.value ? cappedCells.value.map(energyMetric) : []))

const headlineNumbers = computed(() => [
  { key: 'upload', metric: throughputMetric(upload1.value), decimals: 1, percent: false, unit: 'Gbit/s' },
  { key: 'download', metric: throughputMetric(download1.value), decimals: 1, percent: false, unit: 'Gbit/s' },
  { key: 'memory', metric: memoryLoaded.value, decimals: 0, percent: true, unit: 'MiB' },
  { key: 'cpu', metric: cpuEfficiencyMetric(download1.value), decimals: 2, percent: true, unit: t('pandacore.unit.cpu_per_gbit') },
].map((item) => {
  const comparisons = comparisonViews(item.metric)
  const best = comparisons[0]
  return {
    key: item.key,
    leads: best.leads,
    tie: best.tie,
    value: item.percent ? Math.abs(best.deltaPercent) : best.multiplier,
    decimals: item.percent ? 0 : 1,
    prefix: item.percent ? (best.deltaPercent < 0 ? '−' : best.deltaPercent > 0 ? '+' : '') : '',
    suffix: item.percent ? '%' : '×',
    label: t(`pandacore.headline.${item.key}`),
    sub: `${best.caption} · ${fixed(item.metric.pandaCore, item.decimals)} vs ${fixed(best.value, item.decimals)} ${item.unit}`,
    secondary: comparisons.slice(1).map(comparison => `${item.percent ? comparison.deltaText : comparison.multiplierText} · ${comparison.caption}`),
  }
}))

const metricLabels = computed<Record<string, string>>(() => Object.fromEntries(
  allMetrics.value.map(metric => [metric.id, t(`pandacore.metric.${metric.id}`)]),
))

const verdicts = computed(() => {
  const idle = comparisonViews(memoryIdle.value)[0]
  const loaded = comparisonViews(memoryLoaded.value)[0]
  const cpuDown = comparisonViews(cpuEfficiencyMetric(download8.value))[0]
  const cpuUp = comparisonViews(cpuEfficiencyMetric(upload8.value))[0]
  return {
    memory: t('pandacore.resources.verdict_memory', {
      idle: idle.deltaText, idleName: idle.name, loaded: loaded.deltaText, loadedName: loaded.name, connections: platform.value.memory.maxConnections,
    }),
    cpu: t('pandacore.resources.verdict_cpu', {
      down: cpuDown.deltaText, downName: cpuDown.name, up: cpuUp.deltaText, upName: cpuUp.name,
      pandaDown: `${fixed(download8.value.pandaCore.cpuPercentPerGbit, 1)}%`, rivalDown: `${fixed(cpuDown.value, 1)}%`,
    }),
    energy: hasEnergy.value
      ? t('pandacore.resources.verdict_energy', {
          down: comparisonViews(energyMetric(download1.value))[0].deltaText,
          up: comparisonViews(energyMetric(upload1.value))[0].deltaText,
          name: comparisonViews(energyMetric(download1.value))[0].name,
        })
      : '',
  }
})

const matrixCells = computed(() => unlimitedCells.value.map((cell) => {
  const comparisons = comparisonViews(throughputMetric(cell))
  const max = Math.max(cell.pandaCore.gbitPerSecond, cell.singBox.gbitPerSecond, cell.engine2?.gbitPerSecond ?? 0)
  return {
    id: cell.id,
    label: metricLabels.value[cell.id],
    comparisons,
    tie: comparisons[0].tie,
    leads: comparisons[0].leads,
    pandaText: fixed(cell.pandaCore.gbitPerSecond, 1),
    singText: fixed(cell.singBox.gbitPerSecond, 1),
    engine2Text: cell.engine2 ? fixed(cell.engine2.gbitPerSecond, 1) : null,
    pandaWidth: cell.pandaCore.gbitPerSecond / max,
    singWidth: cell.singBox.gbitPerSecond / max,
    engine2Width: cell.engine2 ? cell.engine2.gbitPerSecond / max : 0,
  }
}))

const rawRows = computed(() => allMetrics.value.map((metric) => {
  const group = metric.id.startsWith('memory-') ? 'memory' : metric.id.startsWith('cpu-') ? 'cpu' : metric.id.startsWith('energy-') ? 'energy' : 'throughput'
  const decimals = group === 'energy' ? 5 : 2
  return {
    id: metric.id,
    group,
    label: metricLabels.value[metric.id],
    unit: group === 'memory' ? `${metric.unit} · ${platform.value.memory.metric}` : group === 'cpu' ? t('pandacore.unit.cpu_per_gbit') : metric.unit,
    panda: fixed(metric.pandaCore, decimals),
    sing: fixed(metric.singBox, decimals),
    engine2: metric.engine2 !== undefined ? fixed(metric.engine2, decimals) : null,
    comparisons: comparisonViews(metric),
  }
}))

const rawGroups = computed(() => ['throughput', 'cpu', 'energy', 'memory'].map(id => ({
  id,
  label: t(id === 'throughput' ? 'pandacore.compare.higher_better' : 'pandacore.compare.lower_better'),
  rows: rawRows.value.filter(row => row.group === id),
})).filter(group => group.rows.length > 0))

const raceDescription = computed(() => t('pandacore.race.description', {
  panda: fixed(download1.value.pandaCore.gbitPerSecond, 2),
  comparisons: [...comparisonViews(throughputMetric(download1.value))]
    .sort((a, b) => Number(a.rival === 'engine2') - Number(b.rival === 'engine2'))
    .map(comparison => `${comparison.multiplierText} ${comparison.caption} (${fixed(comparison.value, 2)} Gbit/s)`).join('; '),
  gb: fixed(gigabytesTransferred(download1.value.pandaCore.gbitPerSecond, 10), 1),
}))

// Same order as the guides on /download (DownloadAndGuide.vue).
const platforms = [
  { name: 'Windows', icon: 'simple-icons:windows' },
  { name: 'macOS', icon: 'simple-icons:macos' },
  { name: 'iOS', icon: 'simple-icons:ios' },
  { name: 'Android', icon: 'simple-icons:android' },
  { name: 'Apple TV', icon: 'simple-icons:appletv' },
  { name: 'Linux', icon: 'simple-icons:linux' },
  { name: 'Router', nameZh: '路由器', icon: 'cil:router' },
] as const

const platformLabel = (platform: (typeof platforms)[number]) =>
  locale.value === 'zh' && 'nameZh' in platform ? platform.nameZh : platform.name

/** Same public bucket the Linux download guide uses (PandaCoreLinux.vue). */
const standaloneBaseUrl = 'https://build.bamboe.app/panda-core/latest'

type TerminalPlatform = 'linux' | 'macos' | 'windows'
const terminalPlatforms: { id: TerminalPlatform, label: string }[] = [
  { id: 'linux', label: 'Linux' },
  { id: 'macos', label: 'macOS' },
  { id: 'windows', label: 'Windows' },
]
const terminalPlatform = ref<TerminalPlatform>('linux')

type TerminalArch = 'amd64' | 'arm64'
const archOptions: Record<TerminalPlatform, { id: TerminalArch, label: string }[]> = {
  linux: [{ id: 'amd64', label: 'x86_64' }, { id: 'arm64', label: 'arm64' }],
  macos: [{ id: 'arm64', label: 'Apple Silicon' }, { id: 'amd64', label: 'Intel' }],
  windows: [{ id: 'amd64', label: 'x64' }, { id: 'arm64', label: 'arm64' }],
}
const terminalArch = ref<TerminalArch>('amd64')
watch(terminalPlatform, (platform) => {
  terminalArch.value = archOptions[platform][0]!.id
})

const terminalTarget = computed(() => {
  const platform = terminalPlatform.value
  const arch = archOptions[platform].find(option => option.id === terminalArch.value)
  if (!arch) {
    throw new Error(`Unknown architecture ${terminalArch.value} for ${platform}`)
  }
  const osName = terminalPlatforms.find(option => option.id === platform)!.label
  const file = platform === 'windows'
    ? `pandacore-windows-${arch.id}.exe`
    : `pandacore-${platform === 'macos' ? 'darwin' : 'linux'}-${arch.id}`
  return { platform, file, label: `${osName} ${arch.label}` }
})

type TerminalLine
  = { kind: 'comment', text: string }
    | { kind: 'command', text: string }
    | { kind: 'output', level: string, text: string }

/**
 * Output lines are copied from a real `pandacore` run against a small rule-mode config
 * (timestamps stripped). Keep them in sync with the binary's log wording.
 */
const terminalLines = computed<TerminalLine[]>(() => {
  const { platform, file, label } = terminalTarget.value
  const download = platform === 'windows'
    ? `Invoke-WebRequest ${standaloneBaseUrl}/${file} -OutFile pandacore.exe`
    : `curl -fL ${standaloneBaseUrl}/${file} -o pandacore && chmod +x pandacore`
  const bin = platform === 'windows' ? '.\\pandacore.exe' : './pandacore'
  const downloadComment = platform === 'windows'
    ? t('pandacore.open.cmd_download_comment_windows', { target: label })
    : t('pandacore.open.cmd_download_comment', { target: label })
  return [
    { kind: 'comment', text: downloadComment },
    { kind: 'command', text: download },
    { kind: 'comment', text: t('pandacore.open.cmd_test_comment') },
    { kind: 'command', text: `${bin} -f config.yaml -t` },
    { kind: 'output', level: 'INFO', text: 'panda_config: Config loaded: mode=rule, proxies=6, rules=3' },
    { kind: 'output', level: 'INFO', text: 'pandacore: Configuration test passed' },
    { kind: 'comment', text: t('pandacore.open.cmd_run_comment') },
    { kind: 'command', text: `${bin} -f config.yaml` },
    { kind: 'output', level: 'INFO', text: 'pandacore: panda-core is running' },
    { kind: 'output', level: 'INFO', text: 'panda_listener::mixed: Mixed listener \'mixed\' on 127.0.0.1:7890' },
    { kind: 'output', level: 'INFO', text: 'panda_api: REST API listening on 127.0.0.1:9090' },
  ]
})

const $utils = useUtils()

/** Replay kit: the tun-bench matrix for the platform on screen plus the patch that teaches tun-bench to drive PandaCore. */
const kitGuideOpen = ref(false)
const kitConfig = computed(() => (benchPlatformId.value === 'macos' ? benchConfigDarwin : benchConfigLinux))
const kitConfigName = computed(() => `tun-bench-${benchPlatformId.value === 'macos' ? 'darwin' : 'linux'}.yml`)
const kitPatchName = 'tun-bench-pandacore.patch'
const kitCommands = computed(() => [
  `git clone https://github.com/SagerNet/tun-bench && cd tun-bench && git checkout ${tunBench.tool.commit}`,
  `git apply ${kitPatchName} && go build -o tun-bench .`,
  `sudo ./tun-bench run ${kitConfigName.value} -o round-1.json`,
  'sleep 30',
  `sed 's/implementation: \\[ pandacore, sing-box, mihomo \\]/implementation: [ mihomo, sing-box, pandacore ]/' ${kitConfigName.value} > reversed.yml`,
  'sudo ./tun-bench run reversed.yml -o round-2.json',
  'sleep 30',
  `sudo ./tun-bench run ${kitConfigName.value} -o round-3.json`,
])
const kitMatrixKeys = [
  { key: 'implementation', name: 'implementation' },
  { key: 'stack', name: 'stack' },
  { key: 'mtu', name: 'mtu' },
  { key: 'parallel', name: 'parallel' },
  { key: 'bitrate', name: 'bitrate' },
  { key: 'memory', name: 'type: memory' },
] as const

const kitConfigLines = computed(() => kitConfig.value.split('\n').filter(line => line.length > 0).length)
const kitPatchLines = benchPatch.split('\n').filter(line => line.length > 0).length

function useCopied() {
  const copied = ref(false)
  let timer: ReturnType<typeof setTimeout> | null = null
  const copy = async (text: string) => {
    await $utils.copy(text)
    copied.value = true
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      copied.value = false
      timer = null
    }, 1500)
  }
  const dispose = () => {
    if (timer) {
      clearTimeout(timer)
    }
  }
  return { copied, copy, dispose }
}

const configCopy = useCopied()
const patchCopy = useCopied()
const copyKitConfig = () => configCopy.copy(kitConfig.value)
const copyKitPatch = () => patchCopy.copy(benchPatch)

const copiedLineIndex = ref<number | null>(null)
let copiedLineTimer: ReturnType<typeof setTimeout> | null = null
async function copyCommand(text: string, index: number) {
  await $utils.copy(text)
  copiedLineIndex.value = index
  if (copiedLineTimer) {
    clearTimeout(copiedLineTimer)
  }
  copiedLineTimer = setTimeout(() => {
    copiedLineIndex.value = null
    copiedLineTimer = null
  }, 1500)
}

const openPoints = [
  { title: 'pandacore.open.point_config_title', copy: 'pandacore.open.point_config' },
  { title: 'pandacore.open.point_api_title', copy: 'pandacore.open.point_api' },
  { title: 'pandacore.open.point_free_title', copy: 'pandacore.open.point_free' },
] as const

const facts = [
  { icon: 'lucide:check', key: 'pandacore.final.fact_account' },
  { icon: 'lucide:refresh-cw', key: 'pandacore.final.fact_updates' },
  { icon: 'lucide:message-circle', key: 'pandacore.final.fact_support' },
] as const

/** The comparators are named only by their initials on the public page. */
const rivalName = computed(() => t('pandacore.rival'))
const rival2Name = computed(() => t('pandacore.rival2'))

const methodSteps = computed(() => [
  t('pandacore.method.setup'),
  t('pandacore.method.path'),
  t('pandacore.method.load'),
])

const methodMeta = computed(() => {
  const current = platform.value
  return [
    { label: t('pandacore.method.measured_at'), value: current.measuredAt },
    { label: t('pandacore.method.rounds_label'), value: t('pandacore.method.rounds_value', { rounds: current.rounds }) },
    { label: t('pandacore.method.host_label'), value: current.host },
    { label: t('pandacore.method.tool_label'), value: `${tunBench.tool.name} · ${tunBench.tool.commit}` },
    { label: 'PandaCore', value: current.pandaCoreVersion },
    { label: rivalName.value, value: current.singBoxVersion },
    ...(current.engine2 ? [{ label: rival2Name.value, value: `${current.engine2.version} · ${current.engine2.stack}` }] : []),
    { label: 'MTU', value: `${current.mtu}` },
    { label: t('pandacore.method.schedule_label'), value: t('pandacore.method.schedule_value', { duration: current.durationSeconds, warmup: current.warmupSeconds, workers: current.tunnelWorkers }) },
    { label: t('pandacore.method.memory_schedule_label'), value: t('pandacore.method.memory_schedule_value', { step: current.memory.connectionStep, max: current.memory.maxConnections, bytes: current.memory.payloadBytes }) },
    { label: t('pandacore.method.load_label'), value: t(current.loadMetric === 'energy' ? 'pandacore.method.load_energy' : 'pandacore.method.load_cpu') },
  ]
})

/** Client-side download: nothing is served as a static file. */
function saveTextFile(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }))
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const downloadKitConfig = () => saveTextFile(kitConfigName.value, kitConfig.value, 'application/yaml')
const downloadKitPatch = () => saveTextFile(kitPatchName, benchPatch, 'text/x-diff')

const easeRef = ref<HTMLElement>()
const { revealed: easeRevealed } = useRevealOnce(easeRef, { threshold: 0.25 })
const connectStatusOn = ref(false)
let connectStatusTimer: ReturnType<typeof setTimeout> | null = null
watch(easeRevealed, (isRevealed) => {
  if (isRevealed) {
    connectStatusTimer = setTimeout(() => {
      connectStatusOn.value = true
    }, 950)
  }
})

let revealObserver: IntersectionObserver | null = null

onMounted(() => {
  const targets = Array.from(document.querySelectorAll<HTMLElement>('.pc-shell .reveal:not(.pc-ease)'))
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    targets.forEach(element => element.classList.add('is-visible'))
    return
  }
  revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible')
        revealObserver?.unobserve(entry.target)
      }
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' })
  targets.forEach(element => revealObserver?.observe(element))
})

onBeforeUnmount(() => {
  revealObserver?.disconnect()
  if (connectStatusTimer) {
    clearTimeout(connectStatusTimer)
  }
  if (copiedLineTimer) {
    clearTimeout(copiedLineTimer)
  }
  configCopy.dispose()
  patchCopy.dispose()
})

const pageTitle = computed(() => `${t('pandacore.page_title')} · ${t('site_name')}`)
const pageDescription = computed(() => {
  const upload = comparisonViews(throughputMetric(upload1.value))[0]
  const memory = comparisonViews(memoryLoaded.value)[0]
  return t('pandacore.page_description', { upload: upload.multiplierText, uploadName: upload.name, memory: memory.deltaText, memoryName: memory.name })
})

useSeoMeta({
  title: () => pageTitle.value,
  description: () => pageDescription.value,
  ogTitle: () => pageTitle.value,
  ogDescription: () => pageDescription.value,
  twitterTitle: () => pageTitle.value,
  twitterDescription: () => pageDescription.value,
  twitterCard: 'summary_large_image',
})

useHead({
  title: () => pageTitle.value,
})
</script>

<template lang="pug">
div.pc-shell(:class="{ 'is-zh': locale === 'zh' }")
  a.pc-skip-link(href="#pandacore-main") {{ t('pandacore.skip') }}
  main#pandacore-main.pc-main(tabindex="-1")
    .pc-frame
      section.pc-hero.reveal
        .pc-hero-copy
          p.pc-eyebrow
            span.pc-eyebrow-line
            span {{ t('pandacore.hero.eyebrow') }}
          h1.pc-title
            span.pc-title-line {{ t('pandacore.hero.title_a') }}
            span.pc-title-line.is-accent {{ t('pandacore.hero.title_b') }}
          i18n-t.pc-lead(keypath="pandacore.hero.lead" tag="p")
            template(#leading)
              strong.pc-lead-em {{ t('pandacore.hero.lead_emphasis') }}
          p.pc-hero-note
            Icon.pc-caveat-icon(name="lucide:info" aria-hidden="true")
            span {{ t('pandacore.hero.note') }}
          .pc-actions
            NuxtLink.pc-btn.is-primary(to="/download")
              span {{ t('pandacore.hero.cta_download') }}
              Icon(name="lucide:arrow-up-right" aria-hidden="true")
            a.pc-btn.is-ghost(href="#method") {{ t('pandacore.hero.cta_method') }}
        .pc-hero-stage
          .pc-stage-head
            p.pc-stage-caption {{ t('pandacore.race.caption') }}
            p.pc-stage-note {{ t('pandacore.race.note') }}
          PandaCoreThroughputRace(
            :key="`${platform.id}-${platform.measuredAt}-${download1.pandaCore.gbitPerSecond}`"
            :panda-core="download1.pandaCore.gbitPerSecond"
            :sing-box="download1.singBox.gbitPerSecond"
            :engine2="download1.engine2?.gbitPerSecond"
            panda-core-name="PandaCore"
            :sing-box-name="rivalName"
            :engine2-name="rival2Name"
            :description="raceDescription"
          )

      section.pc-numbers.reveal(:aria-label="t('pandacore.headline.aria')")
        .pc-number(v-for="item in headlineNumbers" :key="item.key" :class="{ 'is-lead': item.leads }")
          p.pc-number-value
            span.pc-number-prefix(v-if="item.prefix") {{ item.prefix }}
            PandaCoreCountUp(:value="item.value" :decimals="item.decimals")
            span.pc-number-suffix {{ item.suffix }}
          p.pc-number-label {{ item.label }}
          p.pc-number-sub {{ item.sub }}
          p.pc-number-sub(v-for="secondary in item.secondary" :key="secondary") {{ secondary }}

      section.pc-section.pc-desktop.reveal(aria-labelledby="pc-throughput-title")
        .pc-section-head
          div
            p.pc-eyebrow
              span.pc-eyebrow-line
              span {{ t('pandacore.throughput.eyebrow') }}
            h2#pc-throughput-title.pc-h2 {{ t('pandacore.throughput.title') }}
          p.pc-section-lead {{ t('pandacore.throughput.lead') }}
        article.pc-panel
          .pc-matrix-head
            .pc-terminal-tabs.is-bench(v-if="benchPlatforms.length > 1" role="tablist" :aria-label="t('pandacore.throughput.platform_label')")
              button.pc-terminal-tab(
                v-for="option in benchPlatforms"
                :key="option.id"
                type="button"
                role="tab"
                :aria-selected="benchPlatformId === option.id"
                :class="{ 'is-active': benchPlatformId === option.id }"
                @click="benchPlatformId = option.id"
              ) {{ option.label }}
            p.pc-matrix-note {{ t('pandacore.throughput.platform_note', { host: platform.host, mtu: platform.mtu }) }}
          .pc-matrix-legend(aria-hidden="true")
            span.pc-matrix-legend-item
              i.pc-cell-dot.is-panda
              | PandaCore
            span.pc-matrix-legend-item
              i.pc-cell-dot.is-sing
              | {{ rivalName }}
            span.pc-matrix-legend-item(v-if="hasEngine2")
              i.pc-cell-dot.is-engine2
              | {{ rival2Name }}
          .pc-matrix.is-four
            .pc-cell(v-for="(cell, index) in matrixCells" :key="cell.id" :class="{ 'is-tie': cell.tie, 'is-loss': !cell.leads && !cell.tie }" :style="{ '--i': index }")
              p.pc-cell-label {{ cell.label }}
              .pc-cell-mults
                p(v-for="(comparison, comparisonIndex) in cell.comparisons" :key="comparison.rival" :class="comparisonIndex === 0 ? 'pc-cell-mult' : 'pc-cell-mult-secondary'")
                  | {{ comparison.tie ? comparison.verdictText : comparison.multiplierText }}
                  small.pc-cell-mult-vs {{ comparison.caption }}
              .pc-cell-meter(aria-hidden="true")
                span.pc-cell-bar.is-panda(:style="{ '--w': cell.pandaWidth }")
                span.pc-cell-bar.is-sing(:style="{ '--w': cell.singWidth }")
                span.pc-cell-bar.is-engine2(v-if="cell.engine2Text" :style="{ '--w': cell.engine2Width }")
              p.pc-cell-values
                span
                  i.pc-cell-dot.is-panda
                  | {{ cell.pandaText }}
                span
                  i.pc-cell-dot.is-sing
                  | {{ cell.singText }}
                span(v-if="cell.engine2Text")
                  i.pc-cell-dot.is-engine2
                  | {{ cell.engine2Text }}
                span.pc-cell-unit Gbit/s
          p.pc-matrix-summary {{ t('pandacore.throughput.summary', { wins: tally.wins, ties: tally.ties, losses: tally.losses }) }}
          p.pc-matrix-tie-note {{ t('pandacore.throughput.tie_note') }}
          details.pc-method-source.pc-raw-block
            summary.pc-method-source-summary
              span {{ t('pandacore.method.raw_title') }}
              span.pc-method-source-meta {{ rawRows.length }} {{ t('pandacore.method.raw_cells') }}
            p.pc-method-source-note {{ t('pandacore.method.raw_copy') }}
            .pc-method-source-bar
              a.pc-btn.is-ghost.is-small(href="/benchmarks/pandacore-tunbench.json" download="pandacore-tunbench.json")
                Icon(name="lucide:download" aria-hidden="true")
                span {{ t('pandacore.method.raw_download') }}
            .pc-raw-scroll
              table.pc-raw
                thead
                  tr
                    th.pc-raw-group-head(aria-hidden="true")
                    th {{ t('pandacore.method.raw_col_cell') }}
                    th PandaCore
                    th {{ rivalName }}
                    th(v-if="hasEngine2") {{ rival2Name }}
                    th {{ t('pandacore.method.raw_col_verdict') }}
                tbody(v-for="group in rawGroups" :key="group.id")
                  tr(v-for="(row, index) in group.rows" :key="row.id")
                    td.pc-raw-group(v-if="index === 0" :rowspan="group.rows.length")
                      span.pc-raw-group-label {{ group.label }}
                    td
                      | {{ row.label }}
                      span.pc-raw-unit {{ row.unit }}
                    td
                      strong {{ row.panda }}
                    td
                      strong {{ row.sing }}
                    td(v-if="hasEngine2")
                      strong {{ row.engine2 }}
                    td
                      span.pc-raw-comparison(v-for="(comparison, comparisonIndex) in row.comparisons" :key="comparison.rival" :class="{ 'is-primary': comparisonIndex === 0 }")
                        strong {{ comparison.multiplierText }} · {{ comparison.caption }}
                        span.pc-raw-delta.is-block(:class="{ 'is-good': comparison.leads }") {{ comparison.deltaText }} · {{ comparison.verdictText }}

      section.pc-section.pc-compare.reveal(aria-labelledby="pc-resources-title")
        .pc-section-head
          div
            p.pc-eyebrow
              span.pc-eyebrow-line
              span {{ t('pandacore.resources.eyebrow') }}
            h2#pc-resources-title.pc-h2 {{ t('pandacore.resources.title') }}
          p.pc-section-lead {{ t('pandacore.resources.lead', { connections: platform.memory.maxConnections }) }}
        .pc-panels
          article.pc-panel.span-7
            header.pc-panel-head
              div
                p.pc-kicker {{ t('pandacore.resources.footprint_kicker') }}
                h3.pc-h3 {{ t('pandacore.resources.footprint') }}
              p.pc-unit-note MiB · {{ t('pandacore.compare.lower_better') }}
            PandaCoreCompareBars(:metrics="footprintMetrics" :labels="metricLabels" :decimals="1" delta-style="percent" :sing-box-name="rivalName" :engine2-name="rival2Name")
            p.pc-verdict {{ verdicts.memory }}
          article.pc-panel.span-5
            header.pc-panel-head
              div
                p.pc-kicker {{ t('pandacore.resources.cpu_kicker') }}
                h3.pc-h3 {{ t('pandacore.resources.cpu') }}
              p.pc-unit-note {{ t('pandacore.unit.cpu_per_gbit') }} · {{ t('pandacore.compare.lower_better') }}
            PandaCoreCompareBars(:metrics="cpuMetrics" :labels="metricLabels" :decimals="2" delta-style="percent" :sing-box-name="rivalName" :engine2-name="rival2Name")
            p.pc-verdict {{ verdicts.cpu }}
          article.pc-panel.span-7(v-if="hasEnergy")
            header.pc-panel-head
              div
                p.pc-kicker {{ t('pandacore.resources.energy_kicker') }}
                h3.pc-h3 {{ t('pandacore.resources.energy') }}
              p.pc-unit-note J/Gbit · {{ t('pandacore.compare.lower_better') }}
            PandaCoreCompareBars(:metrics="energyMetrics" :labels="metricLabels" :decimals="3" delta-style="percent" :sing-box-name="rivalName" :engine2-name="rival2Name")
            p.pc-verdict {{ verdicts.energy }}
          article.pc-panel.span-5(v-if="hasEnergy")
            header.pc-panel-head
              div
                p.pc-kicker {{ t('pandacore.resources.capped_kicker') }}
                h3.pc-h3 {{ t('pandacore.resources.capped') }}
              p.pc-unit-note J/Gbit · {{ t('pandacore.compare.lower_better') }}
            PandaCoreCompareBars(:metrics="cappedEnergyMetrics" :labels="metricLabels" :decimals="4" delta-style="percent" :sing-box-name="rivalName" :engine2-name="rival2Name")
            p.pc-verdict {{ t('pandacore.resources.capped_copy') }}
        p.pc-caveat
          Icon.pc-caveat-icon(name="lucide:triangle-alert" aria-hidden="true")
          span {{ t('pandacore.compare.caveat') }}

      //- This section's reveal class is reactive (useRevealOnce) so Vue owns the whole class list;
      //- the page-level observer above skips it to avoid the two fighting over `is-visible`.
      section.pc-section.pc-ease.reveal(ref="easeRef" :class="{ 'is-visible': easeRevealed }" aria-labelledby="pc-ease-title")
        .pc-section-head
          div
            p.pc-eyebrow
              span.pc-eyebrow-line
              span {{ t('pandacore.ease.eyebrow') }}
            h2#pc-ease-title.pc-h2 {{ t('pandacore.ease.title') }}
          p.pc-section-lead {{ t('pandacore.ease.lead') }}
        .pc-stories
          article.pc-story
            .pc-story-visual
              .connect-visual
                span.connect-ring(aria-hidden="true")
                .connect-toggle(aria-hidden="true")
                  .connect-knob
                p.connect-status
                  span.connect-dot
                  span {{ connectStatusOn ? t('pandacore.ease.connect_on') : t('pandacore.ease.connect_off') }}
            h3.pc-h3 {{ t('pandacore.ease.connect_title') }}
            p.pc-story-copy {{ t('pandacore.ease.connect_copy') }}
          article.pc-story
            .pc-story-visual
              svg.routing-visual(viewBox="0 0 360 200" aria-hidden="true")
                path.routing-path.is-direct(d="M48 100 C 110 100, 190 146, 314 146")
                path.routing-path.is-panda(d="M48 100 C 96 100, 122 54, 164 54")
                path.routing-path.is-panda(d="M198 54 L 314 54")
                circle.routing-device(cx="34" cy="100" r="13")
                circle.routing-node(cx="181" cy="54" r="17")
                circle.routing-site(cx="326" cy="54" r="11")
                circle.routing-site.is-cn(cx="326" cy="146" r="11")
                text.routing-label(x="181" y="88" text-anchor="middle") PandaFan
                text.routing-label(x="326" y="32" text-anchor="middle") {{ t('pandacore.ease.routing_global') }}
                text.routing-label(x="326" y="176" text-anchor="middle") {{ t('pandacore.ease.routing_cn') }}
                text.routing-label.is-muted(x="236" y="168" text-anchor="middle") {{ t('pandacore.ease.routing_direct') }}
            h3.pc-h3 {{ t('pandacore.ease.routing_title') }}
            p.pc-story-copy {{ t('pandacore.ease.routing_copy') }}
          article.pc-story
            .pc-story-visual
              .enhanced-visual(aria-hidden="true")
                .enhanced-switch
                  .enhanced-knob
                  span.enhanced-label {{ t('pandacore.ease.enhanced_label') }}
                .enhanced-link
                .enhanced-apps
                  span.enhanced-app(v-for="n in 9" :key="n" :style="{ '--i': n }")
            h3.pc-h3 {{ t('pandacore.ease.enhanced_title') }}
            p.pc-story-copy {{ t('pandacore.ease.enhanced_copy') }}

      section.pc-section.pc-open.reveal(aria-labelledby="pc-open-title")
        .pc-section-head
          div
            p.pc-eyebrow
              span.pc-eyebrow-line
              span {{ t('pandacore.open.eyebrow') }}
            h2#pc-open-title.pc-h2 {{ t('pandacore.open.title') }}
          p.pc-section-lead {{ t('pandacore.open.lead') }}
        .pc-open-grid
          .pc-terminal
            .pc-terminal-bar
              span.pc-terminal-dot(v-for="n in 3" :key="n" aria-hidden="true")
              span.pc-terminal-title {{ t('pandacore.open.terminal_caption') }}
              .pc-terminal-tabs(role="tablist" :aria-label="t('pandacore.open.tabs_label')")
                button.pc-terminal-tab(
                  v-for="platform in terminalPlatforms"
                  :key="platform.id"
                  type="button"
                  role="tab"
                  :aria-selected="terminalPlatform === platform.id"
                  :class="{ 'is-active': terminalPlatform === platform.id }"
                  @click="terminalPlatform = platform.id"
                ) {{ platform.label }}
              .pc-terminal-tabs.is-arch(role="radiogroup" :aria-label="t('pandacore.open.arch_label')")
                button.pc-terminal-tab(
                  v-for="arch in archOptions[terminalPlatform]"
                  :key="arch.id"
                  type="button"
                  role="radio"
                  :aria-checked="terminalArch === arch.id"
                  :class="{ 'is-active': terminalArch === arch.id }"
                  @click="terminalArch = arch.id"
                ) {{ arch.label }}
            .pc-terminal-body(role="tabpanel")
              .pc-term-line(v-for="(line, index) in terminalLines" :key="index" :class="`is-${line.kind}`" :style="{ '--i': index }")
                template(v-if="line.kind === 'comment'")
                  | {{ line.text }}
                template(v-else-if="line.kind === 'command'")
                  span.pc-term-prompt(aria-hidden="true") {{ terminalPlatform === 'windows' ? '>' : '$' }}
                  span.pc-term-text {{ line.text }}
                  button.pc-term-copy(
                    type="button"
                    :class="{ 'is-copied': copiedLineIndex === index }"
                    :aria-label="copiedLineIndex === index ? t('copied') : t('click_to_copy')"
                    :title="t('click_to_copy')"
                    @click="copyCommand(line.text, index)"
                  )
                    Icon(:name="copiedLineIndex === index ? 'lucide:check' : 'lucide:copy'" aria-hidden="true")
                template(v-else)
                  span.pc-term-level {{ line.level }}
                  | {{ line.text }}
              .pc-term-cursor(aria-hidden="true")
          ul.pc-open-points
            li.pc-open-point(v-for="point in openPoints" :key="point.title")
              h3.pc-h3 {{ t(point.title) }}
              p.pc-story-copy {{ t(point.copy) }}
            li.pc-open-honest
              Icon.pc-caveat-icon(name="lucide:info" aria-hidden="true")
              span {{ t('pandacore.open.honest') }}

      section#method.pc-method.reveal(aria-labelledby="pc-method-title")
        div
          p.pc-eyebrow
            span.pc-eyebrow-line
            span {{ t('pandacore.method.eyebrow') }}
          h2#pc-method-title.pc-h2.is-small {{ t('pandacore.method.title') }}
        .pc-method-body
          p(v-for="(step, index) in methodSteps" :key="index") {{ step }}
          p {{ t('pandacore.method.disclaimer') }}
          dl.pc-method-meta
            div(v-for="item in methodMeta" :key="item.label")
              dt {{ item.label }}
              dd {{ item.value }}
        .pc-method-kit
          h3.pc-h3 {{ t('pandacore.method.kit_title') }}
          p.pc-method-source-note {{ t('pandacore.method.kit_lead') }}
          .pc-method-kit-line
            code.pc-method-kit-cmd sudo ./tun-bench run {{ kitConfigName }}
            button.pc-btn.is-ghost.is-small(type="button" @click="downloadKitConfig")
              span {{ t('pandacore.method.kit_link_config') }}
              Icon(name="lucide:download" aria-hidden="true")
            button.pc-btn.is-ghost.is-small(type="button" @click="downloadKitPatch")
              span {{ t('pandacore.method.kit_link_patch') }}
              Icon(name="lucide:download" aria-hidden="true")
            button.pc-method-kit-readme(type="button" @click="kitGuideOpen = true") {{ t('pandacore.method.kit_readme') }}
          details.pc-method-source
            summary.pc-method-source-summary
              span {{ t('pandacore.method.kit_source') }}
              span.pc-method-source-meta {{ kitConfigName }} · {{ kitConfigLines }} {{ t('pandacore.method.kit_lines') }}
            .pc-method-source-bar
              button.pc-btn.is-ghost.is-small(type="button" :class="{ 'is-copied': configCopy.copied.value }" @click="copyKitConfig")
                Icon(:name="configCopy.copied.value ? 'lucide:check' : 'lucide:copy'" aria-hidden="true")
                span {{ configCopy.copied.value ? t('copied') : t('pandacore.method.kit_copy_all') }}
            pre.pc-method-source-code: code {{ kitConfig }}
          details.pc-method-source
            summary.pc-method-source-summary
              span {{ t('pandacore.method.kit_patch_source') }}
              span.pc-method-source-meta {{ kitPatchName }} · {{ kitPatchLines }} {{ t('pandacore.method.kit_lines') }}
            p.pc-method-source-note {{ t('pandacore.method.kit_patch_note') }}
            .pc-method-source-bar
              button.pc-btn.is-ghost.is-small(type="button" :class="{ 'is-copied': patchCopy.copied.value }" @click="copyKitPatch")
                Icon(:name="patchCopy.copied.value ? 'lucide:check' : 'lucide:copy'" aria-hidden="true")
                span {{ patchCopy.copied.value ? t('copied') : t('pandacore.method.kit_copy_all') }}
            pre.pc-method-source-code: code {{ benchPatch }}

      Sheet(v-model:open="kitGuideOpen")
        SheetContent(side="right" class="flex w-full flex-col gap-0 p-0 sm:max-w-xl")
          SheetHeader(class="border-b px-5 py-4 pr-12 text-left")
            SheetTitle {{ t('pandacore.method.guide_title') }}
            SheetDescription {{ t('pandacore.method.guide_intro') }}
          .pc-guide
            section
              h4 {{ t('pandacore.method.guide_run_title') }}
              pre: code {{ kitCommands.join('\n') }}
              p {{ t('pandacore.method.guide_run_requirements') }}
            section
              h4 {{ t('pandacore.method.guide_how_title') }}
              ol
                li(v-for="n in 5" :key="n") {{ t(`pandacore.method.guide_how_${n}`) }}
            section
              h4 {{ t('pandacore.method.guide_patch_title') }}
              p {{ t('pandacore.method.guide_patch_copy') }}
            section
              h4 {{ t('pandacore.method.guide_knobs_title') }}
              table
                thead
                  tr
                    th {{ t('pandacore.method.guide_knob_var') }}
                    th {{ t('pandacore.method.guide_knob_meaning') }}
                tbody
                  tr(v-for="knob in kitMatrixKeys" :key="knob.key")
                    td: code {{ knob.name }}
                    td {{ t(`pandacore.method.guide_knob_${knob.key}`) }}
            section
              h4 {{ t('pandacore.method.guide_read_title') }}
              ul
                li(v-for="n in 3" :key="n") {{ t(`pandacore.method.guide_read_${n}`) }}

      section.pc-final.reveal(aria-labelledby="pc-final-title")
        h2#pc-final-title.pc-final-title {{ t('pandacore.final.devices_title') }}
        p.pc-final-copy {{ t('pandacore.final.devices_copy') }}
        ul.pc-rail-list
          li(v-for="platform in platforms" :key="platform.name")
            NuxtLink.pc-rail-item(:to="{ path: '/download', query: { os: platform.name } }" prefetch)
              Icon.pc-rail-icon(:name="platform.icon" aria-hidden="true")
              span {{ platformLabel(platform) }}
              Icon.pc-rail-arrow(name="lucide:arrow-up-right" aria-hidden="true")
        ul.pc-facts
          li.pc-fact(v-for="fact in facts" :key="fact.key")
            Icon.pc-fact-icon(:name="fact.icon" aria-hidden="true")
            span {{ t(fact.key) }}
        .pc-final-cta
          h3.pc-final-subtitle {{ t('pandacore.final.title') }}
          p.pc-final-copy {{ t('pandacore.final.copy') }}
          .pc-final-actions
            SignButton.pc-sign-button(defaultState="signup" buttonStyle="main" :redirectIfLoggedIn="true")
            NuxtLink.pc-btn.is-ghost(to="/pricing") {{ t('index.view_pricing') }}
</template>

<style lang="postcss" scoped>

.pc-shell {
  --pc-bg: var(--landing-background);
  --pc-surface: #0b0e0a;
  --pc-surface-2: #161a15;
  --pc-text: #f5f7f1;
  --pc-copy: #969f92;
  --pc-faint: #626a5e;
  --pc-line: rgba(255, 255, 255, 0.08);
  --pc-line-strong: rgba(255, 255, 255, 0.16);
  --pc-green: #7ed321;
  --pc-green-ink: #b9ef6a;
  --pc-green-soft: rgba(126, 211, 33, 0.16);
  --pc-sing: #8b9488;
  --pc-engine2: #c9a85c;
  --pc-on-green: #071006;
  --pc-display: Metropolis, 'SF Pro Display', 'Segoe UI', sans-serif;
  --pc-ease: cubic-bezier(0.16, 1, 0.3, 1);

  position: relative;
  width: 100%;
  min-width: 100%;
  align-self: stretch;
  overflow: clip;
  background: var(--pc-bg);
  color: var(--pc-text);
}

.pc-shell::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(60rem 28rem at 12% -6%, rgba(126, 211, 33, 0.09), transparent 62%),
    linear-gradient(rgba(126, 211, 33, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(126, 211, 33, 0.045) 1px, transparent 1px);
  background-size: auto, 48px 48px, 48px 48px;
  mask-image: linear-gradient(180deg, #000 0%, #000 42%, transparent 100%);
}

.light .pc-shell {
  --pc-surface: #ffffff;
  --pc-surface-2: #eef2f5;
  --pc-text: #1e293b;
  --pc-copy: #64748b;
  --pc-faint: #94a3b8;
  --pc-line: rgba(51, 65, 85, 0.14);
  --pc-line-strong: rgba(51, 65, 85, 0.24);
  --pc-green: #2d5a27;
  --pc-green-ink: #2d5a27;
  --pc-green-soft: rgba(45, 90, 39, 0.12);
  --pc-sing: #94a3b8;
  --pc-engine2: #9a7a1f;
  --pc-on-green: #f6fff0;
}

.light .pc-shell::before {
  background:
    radial-gradient(60rem 28rem at 12% -6%, rgba(45, 90, 39, 0.07), transparent 62%),
    linear-gradient(rgba(51, 65, 85, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(51, 65, 85, 0.07) 1px, transparent 1px);
  background-size: auto, 40px 40px, 40px 40px;
}

.pc-skip-link {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 40;
  padding: 0.8rem 1rem;
  border-radius: 999px;
  background: var(--pc-text);
  color: var(--pc-bg);
  font-size: 0.9rem;
  font-weight: 600;
  transform: translateY(-140%);
  transition: transform 0.25s var(--pc-ease);
}

.pc-skip-link:focus {
  transform: translateY(0);
}

.pc-main {
  position: relative;
  z-index: 1;
}

.pc-frame {
  width: var(--marketing-frame);
  margin-inline: auto;
}

/* ---------- type ---------- */

.pc-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--pc-copy);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1;
}

.pc-eyebrow-line {
  flex: none;
  width: 2.6rem;
  height: 1px;
  background: rgba(126, 211, 33, 0.52);
}

.pc-title,
.pc-h2,
.pc-h3,
.pc-number-value,
.pc-cell-mult,
.pc-final-title {
  font-family: var(--pc-display);
}

.pc-title {
  display: grid;
  margin-top: 1.5rem;
  font-size: clamp(2.7rem, 4.5vw, 4.6rem);
  font-weight: 700;
  line-height: 0.96;
  letter-spacing: -0.05em;
  text-wrap: balance;
}

.pc-title-line.is-accent {
  color: var(--pc-green);
}

.pc-lead {
  max-width: 34rem;
  margin-top: 1.6rem;
  color: var(--pc-copy);
  font-size: clamp(1rem, 1.3vw, 1.15rem);
  line-height: 1.72;
}

.pc-h2 {
  font-size: clamp(2rem, 3.4vw, 3.15rem);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.04em;
  text-wrap: balance;
}

.pc-h2.is-small {
  font-size: clamp(1.7rem, 2.6vw, 2.4rem);
}

.pc-h3 {
  font-size: 1.32rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.pc-kicker {
  margin-bottom: 0.45rem;
  color: var(--pc-faint);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pc-section-lead,
.pc-story-copy,
.pc-verdict,
.pc-method-body p {
  color: var(--pc-copy);
  line-height: 1.68;
}

.pc-shell.is-zh .pc-title,
.pc-shell.is-zh .pc-h2,
.pc-shell.is-zh .pc-h3,
.pc-shell.is-zh .pc-final-title,
.pc-shell.is-zh .pc-final-subtitle {
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans SC', 'Source Han Sans SC', 'Microsoft YaHei', sans-serif;
  letter-spacing: normal;
}

.pc-shell.is-zh .pc-title {
  font-size: clamp(2.4rem, 4vw, 3.7rem);
  line-height: 1.14;
}

@media (min-width: 1081px) {
  /* Keep each Chinese headline phrase on its own line beside the race panel. */
  .pc-shell.is-zh .pc-title-line {
    white-space: nowrap;
  }
}

.pc-shell.is-zh .pc-h2 {
  line-height: 1.2;
}

.pc-shell.is-zh .pc-lead {
  line-height: 1.85;
}

/* ---------- hero ---------- */

.pc-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: clamp(2.5rem, 5vw, 5.5rem);
  padding-block: clamp(3.5rem, 7vw, 6.5rem) clamp(2.5rem, 5vw, 4rem);
}


.pc-lead-em {
  color: var(--pc-text);
  font-weight: 600;
}

.pc-hero-note {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  max-width: 34rem;
  margin-top: 1rem;
  color: var(--pc-faint);
  font-size: 0.84rem;
  line-height: 1.6;
}

.pc-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2.1rem;
}

.pc-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 3rem;
  padding: 0.9rem 1.35rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  transition:
    transform 0.3s var(--pc-ease),
    background-color 0.3s var(--pc-ease),
    border-color 0.3s var(--pc-ease),
    color 0.3s var(--pc-ease);
}

.pc-btn.is-primary {
  background: var(--pc-green);
  color: var(--pc-on-green);
}

.pc-btn.is-primary:hover {
  transform: translateY(-1px);
  background: var(--pc-green-ink);
}

.pc-btn.is-ghost {
  border: 1px solid var(--pc-line-strong);
  color: var(--pc-text);
}

.pc-btn.is-ghost:hover {
  transform: translateY(-1px);
  border-color: rgba(126, 211, 33, 0.5);
  background: var(--pc-green-soft);
}

.pc-btn:active {
  transform: translateY(1px) scale(0.985);
}

.pc-btn:focus-visible,
.pc-sign-button:focus-visible {
  outline: 2px solid rgba(126, 211, 33, 0.6);
  outline-offset: 3px;
}

.pc-hero-stage {
  padding: 1.35rem 1.5rem 1.15rem;
  border: 1px solid var(--pc-line);
  border-radius: 10px;
  background: var(--pc-surface);
}

.pc-stage-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.35rem 1.5rem;
  margin-bottom: 1.35rem;
}

.pc-stage-caption {
  font-size: 0.95rem;
  font-weight: 700;
}

.pc-stage-note {
  color: var(--pc-faint);
  font-size: 0.74rem;
  font-weight: 600;
}

/* ---------- headline numbers ---------- */

.pc-numbers {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid var(--pc-line-strong);
  border-bottom: 1px solid var(--pc-line-strong);
}

.pc-number {
  padding: 2.1rem 1.5rem 2.1rem 0;
  border-right: 1px solid var(--pc-line);
}

.pc-number + .pc-number {
  padding-left: 1.5rem;
}

.pc-number:last-child {
  border-right: 0;
}

.pc-number-value {
  display: flex;
  align-items: baseline;
  font-size: clamp(2.7rem, 4.6vw, 4.5rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.035em;
  font-variant-numeric: tabular-nums;
}

.pc-number-prefix {
  margin-right: 0.04em;
}

.pc-number-suffix {
  margin-left: 0.08em;
  color: var(--pc-copy);
  font-size: 0.5em;
}

.pc-number.is-lead .pc-number-suffix {
  color: var(--pc-green);
}

.pc-number-label {
  margin-top: 1rem;
  color: var(--pc-copy);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pc-number-sub {
  margin-top: 0.4rem;
  color: var(--pc-faint);
  font-size: 0.82rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* ---------- sections ---------- */

.pc-section {
  padding-block: clamp(4rem, 8vw, 7rem) 0;
}

.pc-section-head {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  align-items: end;
  gap: 1.5rem 3rem;
  margin-bottom: clamp(2.2rem, 4vw, 3.4rem);
}

.pc-section-head .pc-h2 {
  margin-top: 1.1rem;
}

.pc-section-lead {
  max-width: 30rem;
  font-size: 1rem;
}

.pc-panels {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: clamp(2.4rem, 4vw, 3.4rem) clamp(2rem, 4vw, 3.5rem);
}

.pc-panel {
  padding-top: 1.35rem;
  border-top: 1px solid var(--pc-line-strong);
  min-width: 0;
}

.pc-panel.span-5 {
  grid-column: span 5;
}

.pc-panel.span-7 {
  grid-column: span 7;
}

.pc-panel-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.pc-unit-note {
  flex: none;
  color: var(--pc-faint);
  font-size: 0.74rem;
  font-weight: 600;
  white-space: nowrap;
}

.pc-verdict {
  margin-top: 1.35rem;
  font-size: 0.93rem;
}

.pc-cpu-note {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 0.85rem;
  margin-top: 1.15rem;
  padding-top: 1rem;
  border-top: 1px dashed var(--pc-line-strong);
  color: var(--pc-copy);
  font-size: 0.85rem;
  line-height: 1.62;
}

.pc-cpu-tag {
  display: inline-flex;
  align-items: center;
  height: 1.35rem;
  padding: 0 0.5rem;
  border: 1px solid var(--pc-line-strong);
  border-radius: 4px;
  color: var(--pc-text);
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.pc-caveat {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  max-width: 44rem;
  margin-top: clamp(2.2rem, 4vw, 3rem);
  color: var(--pc-faint);
  font-size: 0.82rem;
  line-height: 1.6;
}

.pc-caveat-icon {
  flex: none;
  margin-top: 0.15rem;
  color: var(--pc-green);
}

/* ---------- desktop matrix ---------- */

.pc-matrix {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.7rem;
}

.pc-cell {
  padding: 0.9rem 0.95rem 0.85rem;
  border: 1px solid var(--pc-line);
  border-radius: 8px;
  background: var(--pc-surface);
  opacity: 0;
  transform: translateY(0.6rem);
  transition:
    opacity 0.6s ease var(--delay),
    transform 0.7s var(--pc-ease) var(--delay),
    border-color 0.3s ease;
}

.pc-cell:hover {
  border-color: rgba(126, 211, 33, 0.45);
}

.pc-cell.is-tie {
  border-style: dashed;
}

.reveal.is-visible .pc-cell {
  opacity: 1;
  transform: translateY(0);
}

.pc-cell-label {
  color: var(--pc-copy);
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.pc-matrix-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.1rem;
  margin-bottom: 0.9rem;
  color: var(--pc-copy);
  font-size: 0.76rem;
}

.pc-matrix-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 1rem;
  margin-bottom: 1rem;
}

.pc-terminal-tabs.is-bench {
  margin-left: 0;
}

.pc-matrix-note {
  margin: 0;
  color: var(--pc-copy);
  font-size: 0.78rem;
}

.pc-matrix-tie-note {
  margin: 0.9rem 0 0;
  color: var(--pc-copy);
  font-size: 0.76rem;
}

.pc-matrix-summary {
  margin: 1.2rem 0 0;
  color: var(--pc-text);
  font-size: 0.9rem;
  font-weight: 650;
  line-height: 1.6;
}

.pc-matrix-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.pc-cell-dot {
  display: inline-block;
  flex: none;
  width: 0.5rem;
  height: 0.5rem;
  margin-right: 0.28rem;
  border-radius: 2px;
  vertical-align: 0.05em;
}

.pc-matrix-legend .pc-cell-dot {
  margin-right: 0;
}

.pc-cell-dot.is-panda {
  background: var(--pc-green);
}

.pc-cell-dot.is-sing {
  background: var(--pc-sing);
}

.pc-cell-dot.is-engine2 {
  background: var(--pc-engine2);
}

.pc-cell-mults {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.2rem 0.9rem;
  margin-top: 0.4rem;
}

.pc-cell-mult {
  color: var(--pc-green-ink);
  font-size: 1.7rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.pc-cell-mult2 {
  color: var(--pc-copy);
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.pc-cell-mult-vs {
  margin-left: 0.35rem;
  color: var(--pc-faint);
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0;
}

.pc-cell.is-tie .pc-cell-mult {
  color: var(--pc-copy);
  font-size: 1.25rem;
}

.pc-cell-meter {
  display: grid;
  gap: 0.22rem;
  margin-top: 0.75rem;
}

.pc-cell-bar {
  display: block;
  height: 0.28rem;
  width: calc(var(--w) * 100%);
  border-radius: 2px;
  transform-origin: left center;
  transform: scaleX(0);
  transition: transform 0.9s var(--pc-ease) calc(var(--delay) + 0.25s);
}

.reveal.is-visible .pc-cell-bar {
  transform: scaleX(1);
}

.pc-cell-bar.is-panda {
  background: var(--pc-green);
}

.pc-cell-bar.is-sing {
  background: var(--pc-sing);
  opacity: 0.7;
}

.pc-cell-bar.is-engine2 {
  background: var(--pc-engine2);
  opacity: 0.85;
}

.pc-cell-values {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.7rem;
  margin-top: 0.5rem;
  color: var(--pc-faint);
  font-size: 0.7rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.pc-cell-vs {
  opacity: 0.7;
}

/* ---------- ease of use ---------- */

.pc-stories {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2.4rem 2rem;
}

.pc-story {
  display: grid;
  align-content: start;
  gap: 0.75rem;
}

.pc-story .pc-h3 {
  margin-top: 0.6rem;
}

.pc-story-copy {
  font-size: 0.93rem;
}

.pc-story-visual {
  position: relative;
  display: grid;
  place-items: center;
  height: 13.5rem;
  overflow: hidden;
  border: 1px solid var(--pc-line);
  border-radius: 10px;
  background: var(--pc-surface);
}

/* connect */

.connect-visual {
  position: relative;
  display: grid;
  justify-items: center;
  gap: 1.1rem;
}

.connect-ring {
  position: absolute;
  top: 1.95rem;
  left: 50%;
  width: 9rem;
  height: 9rem;
  border: 1px solid var(--pc-green);
  border-radius: 50%;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.5);
}

.pc-ease.is-visible .connect-ring {
  animation: pc-ring 1.5s var(--pc-ease) 0.55s forwards;
}

@keyframes pc-ring {
  0% {
    opacity: 0.85;
    transform: translate(-50%, -50%) scale(0.5);
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.45);
  }
}

.connect-toggle {
  position: relative;
  width: 7.2rem;
  height: 3.9rem;
  border: 1px solid var(--pc-line-strong);
  border-radius: 999px;
  background: var(--pc-surface-2);
  transition:
    background-color 0.5s ease 0.45s,
    border-color 0.5s ease 0.45s;
}

.connect-knob {
  position: absolute;
  top: 0.32rem;
  left: 0.32rem;
  width: 3.14rem;
  height: 3.14rem;
  border-radius: 50%;
  background: #f5f7f1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28);
  transition: transform 0.65s var(--pc-ease) 0.45s;
}

.pc-ease.is-visible .connect-toggle {
  border-color: var(--pc-green);
  background: var(--pc-green);
}

.pc-ease.is-visible .connect-knob {
  transform: translateX(3.3rem);
}

.connect-status {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--pc-copy);
  font-size: 0.82rem;
  font-weight: 600;
}

.connect-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--pc-faint);
  transition:
    background-color 0.3s ease 0.9s,
    box-shadow 0.3s ease 0.9s;
}

.pc-ease.is-visible .connect-dot {
  background: var(--pc-green);
  box-shadow: 0 0 0 4px var(--pc-green-soft);
}

/* routing */

.routing-visual {
  width: min(100%, 22rem);
  height: auto;
}

.routing-path {
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 7 11;
  animation: pc-dash 1.4s linear infinite;
}

.routing-path.is-direct {
  stroke: var(--pc-sing);
  opacity: 0.55;
  animation-duration: 2.2s;
}

.routing-path.is-panda {
  stroke: var(--pc-green);
}

@keyframes pc-dash {
  to {
    stroke-dashoffset: -36;
  }
}

.routing-device {
  fill: var(--pc-surface-2);
  stroke: var(--pc-text);
  stroke-width: 2;
}

.routing-node {
  fill: var(--pc-green);
}

.routing-site {
  fill: var(--pc-surface-2);
  stroke: var(--pc-green);
  stroke-width: 2;
}

.routing-site.is-cn {
  stroke: var(--pc-sing);
}

.routing-label {
  fill: var(--pc-copy);
  font-size: 11px;
  font-weight: 600;
}

.routing-label.is-muted {
  fill: var(--pc-faint);
  font-size: 10px;
}

/* enhanced */

.enhanced-visual {
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 0;
}

.enhanced-switch {
  display: grid;
  justify-items: center;
  gap: 0.55rem;
}

.enhanced-switch::before {
  content: '';
  width: 3rem;
  height: 5rem;
  border: 1px solid var(--pc-line-strong);
  border-radius: 999px;
  background: var(--pc-surface-2);
  transition:
    background-color 0.5s ease 0.4s,
    border-color 0.5s ease 0.4s;
}

.enhanced-knob {
  position: absolute;
  width: 2.3rem;
  height: 2.3rem;
  margin-top: 0.35rem;
  border-radius: 50%;
  background: #f5f7f1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28);
  transition: transform 0.65s var(--pc-ease) 0.4s;
  transform: translateY(2rem);
}

.enhanced-switch {
  position: relative;
}

.pc-ease.is-visible .enhanced-switch::before {
  border-color: var(--pc-green);
  background: var(--pc-green);
}

.pc-ease.is-visible .enhanced-knob {
  transform: translateY(0);
}

.enhanced-label {
  color: var(--pc-copy);
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.enhanced-link {
  width: 3rem;
  height: 1px;
  margin-bottom: 1.4rem;
  background: var(--pc-line-strong);
  transition: background-color 0.4s ease 0.85s;
}

.pc-ease.is-visible .enhanced-link {
  background: var(--pc-green);
}

.enhanced-apps {
  display: grid;
  grid-template-columns: repeat(3, 2.1rem);
  gap: 0.55rem;
  margin-bottom: 1.4rem;
}

.enhanced-app {
  width: 2.1rem;
  height: 2.1rem;
  border: 1px solid var(--pc-line-strong);
  border-radius: 7px;
  background: var(--pc-surface-2);
  transition:
    background-color 0.4s ease calc(0.85s + var(--i) * 70ms),
    border-color 0.4s ease calc(0.85s + var(--i) * 70ms);
}

.pc-ease.is-visible .enhanced-app {
  border-color: var(--pc-green);
  background: var(--pc-green-soft);
}

/* rail + facts */

.pc-rail-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.pc-rail-item {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.62rem 1rem;
  border: 1px solid var(--pc-line-strong);
  border-radius: 999px;
  font-size: 0.88rem;
  font-weight: 600;
  transition: border-color 0.3s ease, background-color 0.3s ease;
}

.pc-rail-item:hover {
  border-color: rgba(126, 211, 33, 0.5);
  background: var(--pc-green-soft);
}

.pc-rail-icon {
  font-size: 1.05rem;
  color: var(--pc-green-ink);
}

.pc-rail-arrow {
  margin-left: -0.15rem;
  font-size: 0.8rem;
  color: var(--pc-faint);
  transition:
    color 0.3s ease,
    transform 0.3s var(--pc-ease);
}

.pc-rail-item:hover .pc-rail-arrow {
  color: var(--pc-green-ink);
  transform: translate(0.1rem, -0.1rem);
}

.pc-rail-item:focus-visible {
  outline: 2px solid rgba(126, 211, 33, 0.6);
  outline-offset: 3px;
}

.pc-facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.2rem 2rem;
  width: 100%;
  max-width: 58rem;
  margin-top: 2.6rem;
  text-align: left;
}

.pc-fact {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  color: var(--pc-copy);
  font-size: 0.92rem;
  line-height: 1.55;
}

.pc-fact-icon {
  flex: none;
  margin-top: 0.2rem;
  color: var(--pc-green);
}

/* ---------- compatible + free ---------- */

.pc-open-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  align-items: start;
  gap: 2.5rem 3rem;
}

.pc-terminal {
  overflow: hidden;
  border: 1px solid var(--pc-line);
  border-radius: 10px;
  background: var(--pc-surface);
}

.pc-terminal-bar {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid var(--pc-line);
}

.pc-terminal-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: var(--pc-line-strong);
}

.pc-terminal-title {
  margin-left: 0.6rem;
  color: var(--pc-faint);
  font-size: 0.74rem;
  font-weight: 600;
}

.pc-terminal-bar {
  flex-wrap: wrap;
  row-gap: 0.5rem;
}

.pc-terminal-tabs {
  display: inline-flex;
  gap: 0.2rem;
  margin-left: auto;
  padding: 0.15rem;
  border: 1px solid var(--pc-line);
  border-radius: 999px;
}

.pc-terminal-tabs.is-arch {
  margin-left: 0.4rem;
}

.pc-term-line.is-command {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.pc-term-text {
  flex: 1;
  min-width: 0;
}

.pc-term-copy {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  margin-top: 0.05rem;
  border-radius: 5px;
  color: var(--pc-faint);
  font-size: 0.85rem;
  transition:
    color 0.2s ease,
    background-color 0.2s ease;
}

.pc-term-copy:hover {
  background: var(--pc-surface-2);
  color: var(--pc-text);
}

.pc-term-copy.is-copied {
  color: var(--pc-green-ink);
}

.pc-term-copy:focus-visible {
  outline: 2px solid rgba(126, 211, 33, 0.6);
  outline-offset: 2px;
}

.pc-terminal-tab {
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  color: var(--pc-copy);
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  transition:
    background-color 0.25s ease,
    color 0.25s ease;
}

.pc-terminal-tab:hover {
  color: var(--pc-text);
}

.pc-terminal-tab.is-active {
  background: var(--pc-green);
  color: var(--pc-on-green);
}

.pc-terminal-tab:focus-visible {
  outline: 2px solid rgba(126, 211, 33, 0.6);
  outline-offset: 2px;
}

.pc-term-level {
  margin-right: 0.55rem;
  color: var(--pc-green-ink);
  font-weight: 700;
}

.pc-term-line.is-output {
  color: var(--pc-copy);
}

.pc-terminal-body {
  padding: 1.05rem 1.2rem 1.15rem;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.8rem;
  line-height: 1.75;
  overflow-wrap: anywhere;
}

.pc-term-line {
  color: var(--pc-text);
  opacity: 0;
  transform: translateY(0.35rem);
}

.pc-term-line.is-comment {
  color: var(--pc-faint);
}

.pc-term-line.is-comment:not(:first-child) {
  margin-top: 0.55rem;
}

.pc-term-prompt {
  flex: none;
  color: var(--pc-green);
}

.reveal.is-visible .pc-term-line {
  animation: pc-term-in 0.55s var(--pc-ease) calc(0.25s + var(--i) * 140ms) both;
}

@keyframes pc-term-in {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pc-term-cursor {
  display: inline-block;
  width: 0.55rem;
  height: 1rem;
  margin-top: 0.3rem;
  background: var(--pc-green);
  opacity: 0;
}

.reveal.is-visible .pc-term-cursor {
  animation: pc-term-blink 1.1s steps(1) 1.4s infinite;
}

@keyframes pc-term-blink {
  0%,
  49% {
    opacity: 1;
  }

  50%,
  100% {
    opacity: 0;
  }
}

.pc-open-points {
  display: grid;
  gap: 1.6rem;
}

.pc-open-point .pc-story-copy {
  margin-top: 0.45rem;
}

.pc-open-honest {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding-top: 1.2rem;
  border-top: 1px dashed var(--pc-line-strong);
  color: var(--pc-faint);
  font-size: 0.82rem;
  line-height: 1.6;
}

/* ---------- method ---------- */

.pc-method {
  display: grid;
  grid-template-columns: minmax(0, 0.42fr) minmax(0, 1fr);
  gap: 1.5rem 3rem;
  margin-top: clamp(4rem, 8vw, 7rem);
  padding-block: clamp(2.8rem, 5vw, 4rem);
  border-top: 1px solid var(--pc-line-strong);
  border-bottom: 1px solid var(--pc-line-strong);
}

.pc-method .pc-h2 {
  margin-top: 1rem;
}

.pc-method-body {
  display: grid;
  gap: 1rem;
  max-width: 40rem;
  font-size: 0.95rem;
}

.pc-method-kit {
  display: grid;
  gap: 0.6rem;
  margin-top: 0.4rem;
  padding: 1.1rem 1.25rem 1.2rem;
  border: 1px solid var(--pc-line);
  border-radius: 10px;
  background: var(--pc-surface);
}

.pc-method-kit-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.3rem;
}

.pc-method-kit-cmd {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--pc-line-strong);
  border-radius: 6px;
  background: var(--pc-surface-2);
  color: var(--pc-text);
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.8rem;
}

.pc-btn.is-small {
  min-height: 2.4rem;
  padding: 0.55rem 1rem;
  font-size: 0.84rem;
}

.pc-method-kit-readme {
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  color: var(--pc-copy);
  font-size: 0.86rem;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 0.2em;
}

.pc-method-kit-readme:hover {
  color: var(--pc-text);
}

.pc-method-source {
  margin-top: 0.5rem;
  border-top: 1px dashed var(--pc-line-strong);
  padding-top: 0.8rem;
}

.pc-method-source-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.6rem;
  cursor: pointer;
  color: var(--pc-text);
  font-size: 0.9rem;
  font-weight: 700;
  list-style: none;
}

.pc-method-source-summary::-webkit-details-marker {
  display: none;
}

.pc-method-source-summary::before {
  content: '';
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  margin-right: 0.15rem;
  border-right: 1.5px solid var(--pc-green);
  border-bottom: 1.5px solid var(--pc-green);
  transform: rotate(-45deg) translateY(-0.1rem);
  transition: transform 0.25s var(--pc-ease);
}

.pc-method-source[open] .pc-method-source-summary::before {
  transform: rotate(45deg) translateY(-0.15rem);
}

.pc-method-source-meta {
  color: var(--pc-faint);
  font-size: 0.76rem;
  font-weight: 600;
}

.pc-method-source-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin: 0.75rem 0 0.5rem;
}

.pc-method-source-note {
  margin-top: 0.6rem;
  color: var(--pc-copy);
  font-size: 0.86rem;
  line-height: 1.6;
}

.pc-method-source-status {
  padding: 0.9rem 1.1rem;
  border: 1px dashed var(--pc-line-strong);
  border-radius: 8px;
  color: var(--pc-faint);
  font-size: 0.84rem;
}

.pc-method-source-status.is-error {
  color: var(--pc-text);
}

.pc-btn.is-copied {
  border-color: rgba(126, 211, 33, 0.5);
  color: var(--pc-green-ink);
}

.pc-method-source-code {
  max-height: 30rem;
  margin: 0;
  padding: 1rem 1.1rem;
  overflow: auto;
  border: 1px solid var(--pc-line);
  border-radius: 8px;
  background: var(--pc-surface-2);
  color: var(--pc-text);
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.74rem;
  line-height: 1.55;
  tab-size: 2;
  white-space: pre;
}

.pc-method-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, auto));
  gap: 1rem 2.5rem;
  margin-top: 0.8rem;
  justify-content: start;
}

.pc-method-meta dt {
  color: var(--pc-faint);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pc-method-meta dd {
  margin-top: 0.3rem;
  color: var(--pc-text);
  font-size: 0.86rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* ---------- final ---------- */

.pc-final {
  display: grid;
  justify-items: center;
  padding-block: clamp(3.5rem, 7vw, 6rem) clamp(4rem, 8vw, 7rem);
  text-align: center;
}

.pc-final-title {
  font-size: clamp(1.9rem, 3.2vw, 2.9rem);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1.05;
}

.pc-final-copy {
  max-width: 34rem;
  margin-top: 1rem;
  color: var(--pc-copy);
  line-height: 1.7;
}

.pc-final .pc-rail-list {
  justify-content: center;
  margin-top: 1.9rem;
}

.pc-final-cta {
  display: grid;
  justify-items: center;
  width: 100%;
  margin-top: clamp(2.6rem, 5vw, 3.6rem);
  padding-top: clamp(2.2rem, 4vw, 3rem);
  border-top: 1px solid var(--pc-line);
}

.pc-final-subtitle {
  font-size: clamp(1.5rem, 2.4vw, 2.05rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.pc-final-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2rem;
}

/* SignButton ships its own boxy style; restyle it to the same pill as .pc-btn.is-primary. */
.pc-sign-button :deep(.sign-up) {
  margin: 0;
}

.pc-sign-button :deep(.sign-up button) {
  height: 3rem !important;
  min-height: 3rem !important;
  padding: 0 1.5rem !important;
  border: 0 !important;
  border-radius: 999px !important;
  background: var(--pc-green) !important;
  box-shadow: none !important;
  color: var(--pc-on-green) !important;
  font-family: var(--pc-display);
  font-size: 0.95rem !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  transition:
    transform 0.3s var(--pc-ease),
    background-color 0.3s var(--pc-ease) !important;
}

.pc-sign-button :deep(.sign-up button:hover) {
  transform: translateY(-1px);
  background: var(--pc-green-ink) !important;
  color: var(--pc-on-green) !important;
}

.pc-sign-button :deep(.sign-up button:active) {
  transform: translateY(1px) scale(0.985);
}

/* ---------- reveal ---------- */

.reveal {
  opacity: 0;
  transform: translate3d(0, 2rem, 0);
  transition:
    opacity 0.75s var(--pc-ease),
    transform 0.75s var(--pc-ease);
}

.reveal.is-visible {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal,
  .pc-cell,
  .pc-cell-bar,
  .connect-toggle,
  .connect-knob,
  .enhanced-knob,
  .enhanced-app {
    transition: none;
  }

  .routing-path,
  .pc-ease.is-visible .connect-ring,
  .reveal.is-visible .pc-term-cursor {
    animation: none;
  }

  .pc-term-line,
  .pc-term-cursor {
    opacity: 1;
    transform: none;
    animation: none;
  }
}

/* ---------- responsive ---------- */

@media (max-width: 767px) {
  .pc-hero {
    grid-template-columns: minmax(0, 1fr);
  }

  .pc-panel.span-5,
  .pc-panel.span-7 {
    grid-column: span 12;
  }

  .pc-method {
    grid-template-columns: minmax(0, 1fr);
  }

  .pc-numbers {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pc-number {
    padding: 1.6rem 1.25rem 1.6rem 0;
    border-bottom: 1px solid var(--pc-line);
  }

  .pc-number:nth-child(2n) {
    border-right: 0;
  }

  .pc-number:nth-child(n + 3) {
    border-bottom: 0;
  }

  .pc-number:nth-child(2n) {
    padding-left: 1.25rem;
  }

  .pc-number:nth-child(2n + 1) {
    padding-left: 0;
  }

  .pc-section-head {
    grid-template-columns: minmax(0, 1fr);
  }

  .pc-stories {
    grid-template-columns: minmax(0, 1fr);
    gap: 2.4rem;
  }

  .pc-facts {
    grid-template-columns: minmax(0, 1fr);
  }

  .pc-open-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .pc-hero-stage {
    padding: 1.1rem 1rem 0.9rem;
  }

  .pc-matrix {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pc-panel-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .pc-method-meta {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ---------- four-cell matrix + raw data ---------- */

.pc-matrix.is-four {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.pc-cell.is-loss .pc-cell-mult {
  color: var(--pc-copy);
}

.pc-cell-unit {
  margin-left: 0.15rem;
  opacity: 0.7;
}

.pc-raw-block {
  margin-top: 1.4rem;
}

.pc-raw-curves {
  display: grid;
  gap: 0.9rem;
  margin: 1.6rem 0 0.2rem;
}



.pc-method-kit {
  max-width: 40rem;
}

@media (min-width: 768px) {
  .pc-method-kit {
    grid-column: 2;
  }
}

.pc-raw-scroll {
  overflow-x: auto;
  border: 1px solid var(--pc-line);
  border-radius: 8px;
  background: var(--pc-surface);
}

.pc-raw {
  width: 100%;
  min-width: 66rem;
  border-collapse: collapse;
  font-size: 0.8rem;
  line-height: 1.45;
}

.pc-raw th,
.pc-raw td {
  padding: 0.6rem 0.8rem;
  border-bottom: 1px solid var(--pc-line);
  text-align: left;
  vertical-align: top;
  white-space: nowrap;
}

.pc-raw th {
  color: var(--pc-faint);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.pc-raw tbody:last-child tr:last-child td {
  border-bottom: 0;
}

.pc-raw tbody + tbody tr:first-child td {
  border-top: 1px solid var(--pc-line-strong);
}

.pc-raw-group-head {
  width: 2.2rem;
  padding: 0;
}

.pc-raw-group {
  width: 2.2rem;
  padding: 0.6rem 0.35rem;
  border-right: 1px solid var(--pc-line);
  text-align: center;
  vertical-align: middle;
}

.pc-raw-group-label {
  display: inline-block;
  writing-mode: vertical-rl;
  color: var(--pc-faint);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.pc-raw td strong {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pc-raw-unit,

.pc-raw-comparison {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.72rem;
}

.pc-raw-comparison.is-primary > strong {
  font-size: 0.85rem;
}

.pc-raw-delta {
  margin-left: 0.4rem;
  color: var(--pc-faint);
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pc-raw-delta.is-block,
.pc-raw-delta-line {
  display: block;
  margin: 0.15rem 0 0;
}

.pc-raw-delta-line .pc-raw-delta {
  margin-left: 0;
}

.pc-raw-delta.is-good {
  color: var(--pc-green);
}

.pc-raw-sep {
  margin: 0 0.45rem;
  color: var(--pc-faint);
}

@media (max-width: 767px) {
  .pc-matrix.is-four {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

/* Kit guide sheet: rendered through a portal, so it uses the site theme tokens, not --pc-* vars. */
.pc-guide {
  display: grid;
  flex: 1 1 auto;
  align-content: start;
  gap: 1.4rem;
  min-height: 0;
  padding: 1.1rem 1.25rem 2rem;
  overflow-y: auto;
  color: var(--foreground);
  font-size: 0.86rem;
  line-height: 1.65;
}

.pc-guide h4 {
  margin: 0 0 0.45rem;
  font-size: 0.95rem;
  font-weight: 700;
}

.pc-guide p,
.pc-guide ol,
.pc-guide ul {
  margin: 0;
  color: var(--muted-foreground);
  font-weight: 400;
}

.pc-guide ol,
.pc-guide ul {
  display: grid;
  gap: 0.4rem;
  padding-left: 1.2rem;
}

.pc-guide pre {
  margin: 0 0 0.6rem;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  background: var(--muted);
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 0.78rem;
  line-height: 1.6;
  white-space: pre;
  overflow-x: auto;
}

.pc-guide table {
  width: 100%;
  margin-bottom: 0.7rem;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.78rem;
}

.pc-guide th,
.pc-guide td {
  padding: 0.4rem 0.5rem 0.4rem 0;
  border-bottom: 1px solid var(--border);
  text-align: left;
  vertical-align: top;
}

.pc-guide th:nth-child(1),
.pc-guide td:nth-child(1) {
  width: 31%;
}

.pc-guide th:nth-child(2),
.pc-guide td:nth-child(2) {
  width: 27%;
}

.pc-guide th {
  color: var(--muted-foreground);
  font-weight: 600;
}

.pc-guide td code {
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 0.74rem;
  word-break: break-word;
}
</style>
